import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import dAppLogo from '../../assets/dashboard-application.png';
import { Button } from '../Button';
import { DashboardLoadingState } from '../LoadingStates';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { getDappHomePath } from '../../utils/dappRoutes';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { AddDappActions } from '../sidebar/AddDappActions';
import { useSidebarData } from '../sidebar/hooks/useSidebarData';

export const DashboardMain = () => {
  usePageTitle('Dashboard | Publishing Portal');
  const navigate = useNavigate();
  const { setVisible } = useWalletModal();
  const [authState] = useAuth();
  const { activePublisher, isLoading: isPublisherLoading } = usePublisher();
  const {
    data: {
      publisherUserJoinId: sidebarPublisherUserJoinId,
      kybStatusData,
      hasKybStatusData,
      dapps,
    },
    isLoading: isSidebarDataLoading,
  } = useSidebarData();
  const wallet = useWallet();
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const isWalletConnected = wallet.connected;
  const isSidebarDataCurrent =
    sidebarPublisherUserJoinId ===
    (activePublisher?.publisherUserJoinId ?? null);
  const isBootstrapLoading =
    isPublisherLoading ||
    (Boolean(activePublisher) &&
      (!isSidebarDataCurrent || isSidebarDataLoading));
  const publisherKybStatus = isSidebarDataCurrent
    ? (kybStatusData?.status ?? activePublisher?.kybStatus)
    : activePublisher?.kybStatus;
  const publisherVerification = getPublisherVerificationGate({
    status: publisherKybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const primaryAction = (() => {
    if (activePublisher && !publisherVerification.isApproved) {
      return (
        <div className="rounded-lg border border-seeker-800 bg-seeker-800/60 px-4 py-3 text-center text-sm text-seeker-300">
          {publisherVerification.actionMessage}
        </div>
      );
    }

    if (!isWalletConnected) {
      return (
        <Button
          variant="primary"
          className="w-full"
          onClick={() => setVisible(true)}
        >
          <Wallet className="size-4" />
          Connect Wallet
        </Button>
      );
    }

    return (
      <AddDappActions
        addLabel="Add a dApp"
        importClassName="font-bold text-white"
        isPublisherVerificationApproved={publisherVerification.isApproved}
        publisherVerificationMessage={publisherVerification.actionMessage}
      />
    );
  })();

  useEffect(() => {
    if (authState.type !== AuthStateType.Authenticated) {
      setIsCheckingStatus(false);
      return;
    }

    if (isBootstrapLoading) {
      setIsCheckingStatus(true);
      return;
    }

    if (!activePublisher) {
      navigate('/publisher-access', { replace: true });
      return;
    }

    setIsCheckingStatus(true);

    if (!hasKybStatusData || !kybStatusData) {
      setIsCheckingStatus(false);
      return;
    }

    const status = kybStatusData.status;
    const verificationApproved = getPublisherVerificationGate({
      status,
      publisherType: activePublisher.publisherType,
      kycKybBypassEnabled: activePublisher.kycKybBypassEnabled,
    }).isApproved;

    if (!verificationApproved) {
      setIsCheckingStatus(false);
      return;
    }

    if (dapps.length > 0) {
      navigate(getDappHomePath(dapps[0]), { replace: true });
      return;
    }

    setIsCheckingStatus(false);
  }, [
    activePublisher,
    authState.type,
    dapps,
    hasKybStatusData,
    isBootstrapLoading,
    kybStatusData,
    navigate,
  ]);

  if (isCheckingStatus) {
    return <DashboardLoadingState />;
  }

  return (
    <div className="flex h-full items-center justify-center overflow-hidden px-5 text-white">
      <div className="flex w-full max-w-80 flex-col items-center gap-12">
        <div className="flex flex-col items-center gap-4">
          <img
            src={dAppLogo}
            alt=""
            aria-hidden="true"
            className="size-32 object-contain"
          />
          <div className="flex flex-col gap-1 text-center">
            <div
              role="heading"
              aria-level={1}
              className="text-xl font-semibold leading-7"
            >
              You haven't added a dApp!
            </div>
            <div className="text-sm leading-5 text-seeker-400">
              Let's add your first dApp to the Solana dApp Store!
            </div>
          </div>
        </div>

        <div className="w-full max-w-64">{primaryAction}</div>
      </div>
    </div>
  );
};
